import { DeleteOutline } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { AccountTypeAPI, FieldDefinitionAPI } from "../../../../api";
import { useGlobalOrganizationContext } from "../../../../hooks/useGlobalOrganizationContext";
import { AccountType } from "../../../../model/AccountType";
import FieldDefinition from "../../../../model/FieldDefinition";
import DataModelAutocomplete from "../../../data/components/DataModelAutocomplete";
import {
  SideDetailsContainer,
  SideDetailsSection,
} from "../../../process/components/side-details";
import messages from "../../../process/messages";
import {
  FieldDefinitionFormElement,
  FormDefinitionType,
  FormElementType,
  FormExecutionType,
} from "../../types";
import FormBuilderElement from "./FormBuilderElement";
import { useFormBuilderContext } from "./FormBuilderProvider";

export const findFieldDefinitionChildren = async (
  id: string,
  elements: FormDefinitionType[],
): Promise<{
  child?: FormDefinitionType;
  parent?: FieldDefinitionFormElement;
  fieldDefinition?: FieldDefinition;
}> => {
  const fieldDefinitionElements = elements.filter(
    (element) => element.type == FormElementType.FieldDefinition,
  ) as FieldDefinitionFormElement[];

  for (const fieldDefElement of fieldDefinitionElements) {
    if (fieldDefElement.field_definition_id) {
      const fieldDefinition = await FieldDefinitionAPI.get(
        fieldDefElement.field_definition_id,
      );
      //TODO make field definition get root parent endpoint
      const found = fieldDefinition.data.elements.find((e) => e.id == id);
      if (found) {
        return {
          child: found,
          parent: fieldDefElement,
          fieldDefinition: fieldDefinition,
        };
      }
    }
  }
  return {};
};

const FieldDefinitionSubjectSelector = ({
  disabled,
  fieldDefinition,
  accountTypes,
}: {
  disabled: boolean;
  fieldDefinition: FieldDefinitionFormElement;
  accountTypes: AccountType[];
}) => {
  const [subjects, setSubjects] = useState<AccountType[]>([]);

  const updateSubjects = useCallback(
    (newSubjects: AccountType[]) => {
      if (!fieldDefinition || !fieldDefinition.field_definition_id) {
        return;
      }

      // need to get diff between the new subjects and the old subjects

      const subjectsToAdd = newSubjects.filter(
        (newSubject) =>
          !subjects?.some((oldSubject) => oldSubject.id === newSubject.id),
      );

      const subjectsToRemove = subjects?.filter(
        (oldSubject) =>
          !newSubjects.some((newSubject) => newSubject.id === oldSubject.id),
      );

      // call the AccountTypeAPI to add and remove the appropriate subjects
      for (let a = 0; a < subjectsToAdd.length; a++) {
        const subjectToAdd = subjectsToAdd[a];
        AccountTypeAPI.addSelectedFieldDefinition(
          subjectToAdd.id,
          fieldDefinition.field_definition_id,
        );
      }

      for (let a = 0; a < subjectsToRemove?.length; a++) {
        const subjectToRemove = subjectsToRemove[a];
        AccountTypeAPI.removeSelectedFieldDefinition(
          subjectToRemove.id,
          fieldDefinition.field_definition_id,
        );
      }

      setSubjects(newSubjects);
    },
    [fieldDefinition, subjects],
  );

  useEffect(() => {
    if (!fieldDefinition || !fieldDefinition.field_definition_id) {
      return;
    }

    FieldDefinitionAPI.getSubjects(fieldDefinition.field_definition_id).then(
      (result) => {
        setSubjects(result);
      },
    );
  }, [fieldDefinition]);

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={accountTypes}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Link to Account Type"
          variant="standard"
          placeholder={subjects?.length ? "" : "Select Account Type"}
        />
      )}
      getOptionLabel={(option) => option.name}
      value={subjects ?? []}
      getOptionKey={(option) => option.id}
      onChange={(e, newValues) => {
        if (newValues) {
          updateSubjects(newValues);
        } else {
          updateSubjects([]);
        }
      }}
      isOptionEqualToValue={(option, value) => {
        return option.id === value.id && option.name === value.name;
      }}
    />
  );
};

const FormBuilderSideSectionContainer = () => {
  const { elements, executionData, selectedElementId, onFormElementChange } =
    useFormBuilderContext();

  const { organization } = useGlobalOrganizationContext();

  const intl = useIntl();
  const process = intl.formatMessage(messages.process.props);

  const labelRef = useRef<HTMLInputElement>();
  const [editingFieldDef, setEditingFieldDef] = useState(false);
  const element: FormDefinitionType | undefined = useMemo(() => {
    const e = elements.find((element) => element.id == selectedElementId);
    if (!e && selectedElementId) {
      return elements
        .filter(
          (e) =>
            e.type == FormElementType.FieldDefinition &&
            (e as FieldDefinitionFormElement).elements,
        )
        .flatMap((e) => (e as FieldDefinitionFormElement).elements!)
        .find((e) => e.id == selectedElementId);
    } else {
      setEditingFieldDef(false);
      return e;
    }
  }, [elements, selectedElementId]);
  const execution: FormExecutionType | undefined = useMemo(() => {
    return executionData.find((e) => e.id == selectedElementId);
  }, [executionData, selectedElementId]);

  const [accountTypes, setAccountTypes] = useState<AccountType[]>([]);

  // get the initial value and then do not update it afterwards
  const originalState = useMemo(() => {
    if (!element) return false;
    const fieldDefElement = element as FieldDefinitionFormElement;
    return fieldDefElement.global_restore;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!element || !organization) return;
    AccountTypeAPI.byOrganizationId(organization.id).then((result) => {
      setAccountTypes(result);
    });
  }, [organization, element]);

  useEffect(() => {
    if (element?.id) {
      labelRef.current?.focus();
    }
  }, [element?.id]);

  return (
    <SideDetailsContainer
      maxWidth={600}
      scrollable
      sx={{
        height: "100%",
      }}
    >
      <SideDetailsSection>
        {element && (
          <FormGroup sx={{ gap: 2, flex: 1 }}>
            <TextField
              inputRef={labelRef}
              fullWidth
              label={"Label"}
              value={element?.label ?? ""}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => {
                onFormElementChange({
                  changeType: "update",
                  newElement: {
                    ...element,
                    label: e.target.value,
                  },
                });
              }}
            />
            <TextField
              fullWidth
              label={"Description"}
              value={element?.description ?? ""}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => {
                onFormElementChange({
                  changeType: "update",
                  newElement: {
                    ...element,
                    description: e.target.value,
                  },
                });
              }}
            />
            <TextField
              fullWidth
              label={"Variable Name"}
              value={element?.name ?? ""}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => {
                onFormElementChange({
                  changeType: "update",
                  newElement: {
                    ...element,
                    name: e.target.value,
                  },
                });
              }}
            />
            {element.type !== FormElementType.Heading && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!element.required}
                    onChange={(e) => {
                      onFormElementChange({
                        changeType: "update",
                        newElement: {
                          ...element,
                          required: e.target.checked,
                        },
                      });
                    }}
                  />
                }
                label={"Required"}
              />
            )}
            <DataModelAutocomplete
              modelRefs={element.modelRefs}
              onChange={(modelFields) => {
                onFormElementChange({
                  changeType: "update",
                  newElement: {
                    ...element,
                    modelRefs: modelFields,
                  },
                });
              }}
            />
            <TextField
              fullWidth
              label={"Link"}
              value={element?.link ?? ""}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => {
                onFormElementChange({
                  changeType: "update",
                  newElement: {
                    ...element,
                    link: e.target.value,
                  },
                });
              }}
            />
            {element.type === FormElementType.Heading && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!element.isAnchored}
                    onChange={(e) => {
                      onFormElementChange({
                        changeType: "update",
                        newElement: {
                          ...element,
                          isAnchored: e.target.checked,
                        },
                      });
                    }}
                  />
                }
                label={"Anchored"}
              />
            )}
            <FormBuilderElement
              variant="side-section"
              element={element}
              executionData={execution}
              onChange={onFormElementChange}
            />
            {element.type == FormElementType.FieldDefinition && (
              <>
                <Divider />

                <Typography>Field Definition Properties</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!element.global_restore}
                      onChange={(e) => {
                        onFormElementChange({
                          changeType: "update",
                          newElement: {
                            ...element,
                            global_restore: e.target.checked,
                          },
                        });
                      }}
                    />
                  }
                  label={"Save and Auto-Fill for all Account Types"}
                />
                <FieldDefinitionSubjectSelector
                  disabled={element.global_restore ?? false}
                  fieldDefinition={element as FieldDefinitionFormElement}
                  accountTypes={accountTypes}
                />
                {element.global_restore ? (
                  <Alert severity="success">
                    <Typography>
                      All Account Types and users will have the data
                      automatically saved and auto-filled
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="info">
                    <Typography>
                      Only Account Types that are associated with this Field
                      Definition above will have the data automatically saved
                      and auto-filled
                    </Typography>
                  </Alert>
                )}
                {originalState != element.global_restore && (
                  <Alert severity="warning">
                    <Typography>
                      You must Publish the {process} in order for these changes
                      to be reflected.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
            <Divider />
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: editingFieldDef ? "space-between" : "flex-end",
              }}
            >
              {editingFieldDef && (
                <Typography>Editing Field Definition</Typography>
              )}
              <Tooltip title={"Delete Element"} placement={"bottom"}>
                <IconButton
                  color={"default"}
                  onClick={() => {
                    onFormElementChange({
                      changeType: "remove",
                      elementId: element.id,
                    });
                  }}
                >
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
            </Box>
          </FormGroup>
        )}
      </SideDetailsSection>
    </SideDetailsContainer>
  );
};

export default FormBuilderSideSectionContainer;
